import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVerticalIcon, TrashIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MOBILE_ICON_BUTTON_XS } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface DraftStep {
  /** Stable id for sortable reordering. */
  id: string;
  action: string;
  uses: string;
  creates: string;
  requires_approval: boolean;
}

interface WorkflowEditorStepRowProps {
  step: DraftStep;
  idx: number;
  canRemove: boolean;
  onUpdate: (patch: Partial<DraftStep>) => void;
  onRemove: () => void;
}

/**
 * One step row in the editor. Sortable via @dnd-kit/sortable — the parent
 * wraps the list in <DndContext> + <SortableContext>. The drag handle is
 * the dotted-grip icon on the left; the rest of the card is non-draggable
 * so users can edit fields without accidentally initiating a drag.
 *
 * Layout (Talvo-rhythm bordered card):
 *   ┌─ rounded-lg border p-4 ──────────────────────────────────────┐
 *   │ ⋮⋮ │ 01 │ <Label>Action *</Label>                             │
 *   │              <Input bordered />                                │
 *   │              ┌─ Takes ────────┐ ┌─ Makes ────────┐            │
 *   │              │ <Input>        │ │ <Input>        │            │
 *   │              └────────────────┘ └────────────────┘            │
 *   │              ☐ Pause for my approval                          │
 *   │                                                            🗑 │
 *   └────────────────────────────────────────────────────────────────┘
 */
export function WorkflowEditorStepRow({
  step,
  idx,
  canRemove,
  onUpdate,
  onRemove,
}: WorkflowEditorStepRowProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative py-5 transition-colors',
        idx > 0 && 'border-t border-border/40',
        isDragging && 'bg-card shadow-elevation-2 z-10 rounded-md -mx-2 px-2',
      )}
    >
      <div className="flex items-start gap-3">
        {/* Drag handle — only this triggers a drag */}
        <button
          type="button"
          aria-label={`Reorder step ${idx + 1}`}
          {...attributes}
          {...listeners}
          className={cn(
            'mt-1 shrink-0 cursor-grab inline-flex items-center justify-center rounded-md text-muted-foreground/40 opacity-0 transition hover:bg-muted/40 hover:text-foreground/70 active:cursor-grabbing group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            MOBILE_ICON_BUTTON_XS,
          )}
        >
          <DotsSixVerticalIcon className="size-4" weight="bold" />
        </button>

        {/* Step number — plain numeral, not mono */}
        <span className="mt-2 shrink-0 text-sm text-muted-foreground/55 w-5">{idx + 1}.</span>

        {/* Fields */}
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <Label htmlFor={`step-action-${step.id}`}>
              Action <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`step-action-${step.id}`}
              size="sm"
              value={step.action}
              onChange={(e) => onUpdate({ action: e.target.value })}
              placeholder="e.g. Draft the headline"
              maxLength={80}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`step-takes-${step.id}`}>Takes</Label>
              <Input
                id={`step-takes-${step.id}`}
                size="sm"
                value={step.uses}
                onChange={(e) => onUpdate({ uses: e.target.value })}
                placeholder="prior step output"
              />
            </div>
            <div>
              <Label htmlFor={`step-makes-${step.id}`}>Makes</Label>
              <Input
                id={`step-makes-${step.id}`}
                size="sm"
                value={step.creates}
                onChange={(e) => onUpdate({ creates: e.target.value })}
                placeholder="this step's output"
              />
            </div>
          </div>
          <label
            className={cn(
              'flex w-fit cursor-pointer items-center gap-2 transition-colors',
              step.requires_approval
                ? 'text-skill/80'
                : 'text-muted-foreground hover:text-foreground/80',
            )}
          >
            <Checkbox
              checked={step.requires_approval}
              onCheckedChange={(checked) => onUpdate({ requires_approval: checked === true })}
              aria-label="Pause for my approval"
            />
            <span className="text-detail">Pause for my approval</span>
          </label>
        </div>

        {/* Remove */}
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove step ${idx + 1}`}
            className={cn(
              'shrink-0 inline-flex items-center justify-center rounded-md text-muted-foreground/50 opacity-0 transition-opacity hover:bg-muted/60 hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              MOBILE_ICON_BUTTON_XS,
            )}
          >
            <TrashIcon className="size-3.5" weight="regular" />
          </button>
        )}
      </div>
    </li>
  );
}
